import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthData } from './../models/authData.interface';
import { AuthProvider, Constants } from './../utils/Constants';
import { ResponseData } from './../models/responseData.interface';
import { ModuloPermiso, UserData } from './../models/userData.interface';
import Swal from 'sweetalert2';
import { RecoverPassData } from '../models/recoverPassData.interface';
import { Observable, timeout } from 'rxjs';
import { UpdatePassData } from './../models/updatePassData.interface';
import { RestablecerPassData } from '../models/restablecerPassData.interface';
import { ROLES_CONFIG, RolUsuario } from '../models/roles.enum';
import { MsalService } from '@azure/msal-angular';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private msalService = inject(MsalService);

  /**
   * * login
   * @param authData
   * @returns
   */
  login(authData: AuthData): Observable<ResponseData> {
    //Ejecutamos y retornamos el inicio de sesión
    return this.http.post<ResponseData>(
      `${Constants.baseUrl}auth/login`,
      authData
    );
  }

  /**
   * * loginSso — inicio de sesión con cuenta Microsoft (Entra ID)
   *
   * Envía el token que MSAL obtuvo de Entra ID. El backend lo valida contra las JWKS de
   * Microsoft (firma, emisor, audiencia, tenant y expiración) y, solo si es válido, responde
   * con la misma estructura que `login()`: token de SCO, refresh token y permisos.
   *
   * El token de Entra se usa una única vez aquí y se descarta: no se guarda en localStorage.
   *
   * @param entraToken access token emitido por Entra ID para el scope de esta API
   */
  loginSso(entraToken: string): Observable<ResponseData> {
    return this.http.post<ResponseData>(`${Constants.baseUrl}auth/sso`, {
      token: entraToken,
    });
  }

  /**
   * * recoverPass
   * @param recoverPassData
   * @returns
   */
  recoverPass(recoverPassData: RecoverPassData): Observable<ResponseData> {
    return this.http.post<ResponseData>(
      `${Constants.baseUrl}auth/recoverpassByUsername`,
      recoverPassData
    );
  }

  /**
   * * validaCodigo
   * @param codigo
   * @returns
   */
  validaCodigo(codigo: string | null | undefined): Observable<ResponseData> {
    return this.http.get<ResponseData>(
      `${Constants.baseUrl}auth/validaCodigo/${codigo}`
    );
  }

  /**
   * * actualizaPass
   * @param updatePassData
   * @returns
   */
  actualizaPass(updatePassData: UpdatePassData): Observable<ResponseData> {
    return this.http.put<ResponseData>(
      `${Constants.baseUrl}auth/actualizaPass`,
      updatePassData
    );
  }

  /**
   * * restablecePass
   * @param restablecerPassData
   * @returns
   */
  restablecePass(
    restablecerPassData: RestablecerPassData
  ): Observable<ResponseData> {
    return this.http.put<ResponseData>(
      `${Constants.baseUrl}auth/restablecePass`,
      restablecerPassData
    );
  }

  refreshTokens(): Observable<ResponseData> {
    const refreshToken = this.getRefreshToken();
    const REFRESH_TIMEOUT_MS = 60_000;
    return this.http
      .post<ResponseData>(`${Constants.baseUrl}auth/refresh`, { refreshToken })
      .pipe(timeout(REFRESH_TIMEOUT_MS));
  }

  logoutServer(): Observable<ResponseData> {
    const refreshToken = this.getRefreshToken();
    return this.http.post<ResponseData>(
      `${Constants.baseUrl}auth/logout`, 
      { refreshToken }
    );
  }

  /**
   * * Método para guardar el token en el cliente
   * @param token
   */
  storeToken(token: string): void {
    localStorage.setItem(Constants.tokenKey, token);
  }

  storeRefreshToken(refreshToken: string): void {
    localStorage.setItem(Constants.refreshTokenKey, refreshToken);
  }

  /**
   * * Metodo para guardar los datos del usuario
   * @param userData
   */
  storeUserData(userData: UserData): void {
    localStorage.setItem(Constants.userKey, JSON.stringify(userData));
  }

  /**
   * * Método para tomar el token guardado (retorna NULL si no hay token guardado)
   * @returns
   */
  getToken(): string | null {
    return localStorage.getItem(Constants.tokenKey);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(Constants.refreshTokenKey);
  }

  /**
   * * Método para tomar los datos del usuario guardados
   * @returns
   */
  getUserData(): UserData | null {
    const strUserData: string | null = localStorage.getItem(Constants.userKey);
    return strUserData === null
      ? strUserData
      : (JSON.parse(strUserData) as UserData);
  }

  /**
   * * Registra con qué método se inició la sesión activa.
   * El logout lo consulta para saber si además debe cerrar la sesión de MSAL.
   */
  storeAuthProvider(provider: AuthProvider): void {
    localStorage.setItem(Constants.authProviderKey, provider);
  }

  /**
   * * Devuelve el origen de la sesión activa. Si no está marcado se asume 'local',
   * que es el comportamiento de todas las sesiones anteriores a Entra ID.
   */
  getAuthProvider(): AuthProvider {
    return localStorage.getItem(Constants.authProviderKey) === 'entra'
      ? 'entra'
      : 'local';
  }

  clearSession(): void {
    localStorage.removeItem(Constants.tokenKey);
    localStorage.removeItem(Constants.refreshTokenKey);
    localStorage.removeItem(Constants.userKey);
    localStorage.removeItem(Constants.authProviderKey);
  }

  private ejecutarCierreSesion(): void {
    // Se consulta ANTES de clearSession(), que borra la marca de origen.
    if (this.getAuthProvider() === 'entra') {
      this.cerrarSesionMsal();
    }
    this.clearSession();
    this.router.navigate(['login']);
  }

  /**
   * * Limpia la caché local de MSAL al cerrar sesión en SCO.
   *
   * Se usa `clearCache()` y NO `logoutPopup()` / `logoutRedirect()` a propósito: esos cierran la
   * sesión de Microsoft del usuario en TODO el navegador (Outlook, Teams, etc.). El logout de SCO
   * debe terminar la sesión de SCO, no sacar a la persona de su cuenta corporativa.
   */
  private cerrarSesionMsal(): void {
    try {
      const resultado = this.msalService.instance.clearCache();
      // clearCache() devuelve una promesa; si MSAL no llegó a inicializarse, se ignora:
      // la sesión de SCO debe cerrarse igual.
      Promise.resolve(resultado).catch(() => undefined);
    } catch {
      // MSAL no inicializado (sesión local). No hay nada que limpiar.
    }
  }

  /**
   * * Método para eliminar el token del cliente
   * @param confirm Si se marca en true, se muestra una modal
   */
  logout(confirm: boolean): void {
    if (confirm) {
      Swal.fire({
        title: 'Cerrar sesión',
        text: '¿Deseas cerrar sesión?',
        icon: 'question',
        showCancelButton: true,
        cancelButtonText: 'No, cancelar',
        confirmButtonText: 'Si, cerrar sesión',
      }).then((result) => {
        if (result.isConfirmed) {
          this.logoutServer().subscribe({
            next: () => this.ejecutarCierreSesion(),
            error: () => this.ejecutarCierreSesion(),
          });
        }
      });
    } else {
      this.logoutServer().subscribe({
        next: () => this.ejecutarCierreSesion(),
        error: () => this.ejecutarCierreSesion(),
      });
    }
  }

  /**
   * * Método para determinar si estamos autenticados o no
   * @returns
   */
  isAuth(): boolean {
    return this.getToken() === null ? false : true;
  }

  private get permisos(): ModuloPermiso[] {
    return this.getUserData()?.permisos ?? [];
  }

  private hasPermission(modulo: string, permiso: string): boolean {
    const m = this.permisos.find(p => p.codigoModulo === modulo);
    return !!m && m.permisos?.includes(permiso);
  }

  hasModulePermission(modulo: string, permiso: string): boolean {
    return this.hasPermission(modulo, permiso);
  }

  hasRole(allowedRoles: number[]): boolean {
    const user = this.getUserData();
    const userRol = user?.idRol ?? 0;
    return allowedRoles.includes(userRol);
  }

  
  get isAnyAdmin(): boolean {
    return this.hasRole(ROLES_CONFIG.ADMIN_ACCESS);
  }

 
  get isServicios(): boolean {
    return this.hasRole(ROLES_CONFIG.SERVICIOS_ONLY);
  }


  get isRestrictedAdmin(): boolean {
    return this.hasRole(ROLES_CONFIG.RESTRICTED_ONLY);
  }

 
  get isAdminNoRestricted(): boolean {
    return this.hasRole(ROLES_CONFIG.NO_RESTRICTED);
  }

 
  get canAccessEmpleados(): boolean {
    return this.hasPermission('EMPLEADOS', 'VIEW') || this.hasRole(ROLES_CONFIG.EMPLEADOS);
  }
  get canAccessCatalogos(): boolean {
    return this.hasPermission('CATALOGOS', 'VIEW') || this.hasRole(ROLES_CONFIG.CATALOGOS);
  }
  get canAccessListadoServicios(): boolean {
    return this.hasPermission('SERVICIOS_PGO', 'VIEW') || this.hasRole(ROLES_CONFIG.LISTADO_SERVICIOS);
  }
  get canAccessClientes(): boolean {
    return this.hasPermission('CLIENTES', 'VIEW') || this.hasRole(ROLES_CONFIG.CLIENTES);
  }
  get canAccessProveedores(): boolean {
    return this.hasPermission('PROVEEDORES', 'VIEW') || this.hasRole(ROLES_CONFIG.PROVEEDORES);
  }
  get canAccessReporteProyectos(): boolean {
    return this.hasPermission('REPORTE_PROYECTOS', 'VIEW') || this.hasRole(ROLES_CONFIG.REPORTE_PROYECTOS);
  }
  get canAccessServiciosProyectos(): boolean {
    return this.hasPermission('PROYECTOS', 'VIEW') || this.hasRole(ROLES_CONFIG.SERVICIOS_PROYECTOS);
  }
  get canAccessSolicitudes(): boolean {
    return this.hasPermission('SOLICITUDES', 'VIEW') || this.hasRole(ROLES_CONFIG.SOLICITUDES);
  }

  /** Rol PMO: ver solo solicitudes de proyectos donde es project manager (backend aplica el filtro). */
  get shouldScopeSolicitudesToPmProjects(): boolean {
    return this.hasRole([RolUsuario.PMO]);
  }

  /** Rol PMO: ver solo órdenes de venta de proyectos donde es project manager (backend aplica el filtro). */
  get shouldScopeOrdenesVentaToPmProjects(): boolean {
    return this.hasRole([RolUsuario.PMO]);
  }
  get canAccessAsignaciones(): boolean {
    return this.hasPermission('ASIGNACIONES', 'VIEW') || this.hasRole(ROLES_CONFIG.ASIGNACIONES);
  }
  get canAccessReportes(): boolean {
    return this.hasPermission('REPORTES', 'VIEW') || this.hasRole(ROLES_CONFIG.REPORTES);
  }
  get canAccessReportesColab(): boolean {
    return this.hasPermission('REPORTES_COLAB', 'VIEW') || this.hasRole(ROLES_CONFIG.REPORTES_COLAB);
  }
  get canAccessReportesBitacora(): boolean {
    return this.hasPermission('REPORTES_BITACORA', 'VIEW') || this.hasRole(ROLES_CONFIG.REPORTES_BITACORA);
  }
  get canAccessCierreBitacora(): boolean {
    return (
      this.hasPermission('CIERRE_BITACORA', 'VIEW') ||
      this.hasRole(ROLES_CONFIG.CIERRE_BITACORA) ||
      this.hasPermission('REPORTES_BITACORA', 'VIEW') ||
      this.hasRole(ROLES_CONFIG.REPORTES_BITACORA)
    );
  }
  get canAccessOrdenVenta(): boolean {

    return this.hasRole(ROLES_CONFIG.ORDEN_VENTA) || this.hasPermission('ORDEN_VENTA', 'VIEW');
  }

  get canEditListadoServicios(): boolean {

    return this.hasPermission('SERVICIOS_PGO', 'EDIT') || this.hasRole(ROLES_CONFIG.LISTADO_SERVICIOS_EDIT);
  }

  /** Auditoría (creación/modificación) en listado Servicios PGO: solo rol 8 (ADMIN_SERVICIOS). */
  get canViewAuditoriaServiciosPgo(): boolean {
    return this.hasRole([RolUsuario.ADMIN_SERVICIOS]);
  }

 
  get canAccessProyectoControlFases(): boolean {
    return (
      this.hasPermission('INVENTARIO', 'VIEW') ||
      this.hasRole([RolUsuario.ADMIN_SERVICIOS])
    );
  }

 
  get canAccessDisponibilidadRecursos(): boolean {
    return (
      this.hasPermission('DISPONIBILIDAD', 'VIEW') ||
      this.hasRole([RolUsuario.ADMIN_SERVICIOS])
    );
  }
    
  get canEditServiciosProyectos(): boolean {

    return this.hasPermission('PROYECTOS', 'EDIT') || this.hasRole(ROLES_CONFIG.SERVICIOS_PROYECTOS_EDIT);
  }

  get canEditOrdenCompra(): boolean {
 
    return this.hasPermission('ORDEN_COMPRA', 'EDIT');
  }
}